import { hasArmedCheckout } from "@/composables/useBillingConfirmation.js";

// Which accounts this browser session has already sent off to a checkout.
//
// Onboarding's plan step must not be shown to someone who arrived from the
// marketing page having already picked a plan: DashboardView's
// checkPendingSubscription pops that stash and launches the checkout from
// mounted(), BEFORE the wizard is on screen, so by the time the wizard could
// look for the stash it is already gone. The launch therefore records itself
// here, and the wizard reads session state rather than trying to re-read a
// value that no longer exists. The post-onboarding hand-off reads the same
// state before launching, so a pick made either side of that gap cannot turn
// into two checkouts.
//
// A SET, not a single owner. More than one account can sign into one tab, and
// a launch recorded for one of them must not be erased by the next: the first
// account's checkout is still pending, and forgetting it would offer them a
// second one if they came back. Nothing is ever removed — "a checkout was
// launched" cannot become untrue, and the outcome arrives as billing/features,
// not as a reset here.
let launchedUserIds = new Set();

// Set when a launch happened that could not be attributed to an account. Kept
// separate from the set so an unattributed launch is never mistaken for a
// particular account's, while still forcing the conservative answer below.
let unattributedLaunch = false;

// Persisted for the same reason the armed plan is: a web checkout is a
// full-page redirect, which wipes the module state but not the tab. Signing
// out does not reload the tab either, so without the uids the next account
// inherits the first one's latch and is quietly denied the plan step over a
// purchase that was never theirs. (Not localStorage: this is session state,
// and a new tab is a new session.)
const OWNERS_KEY = "al.checkoutLaunchedUids";

function readStoredOwners() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(OWNERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const ids = parsed.filter((id) => typeof id === "string" && id !== "");
    return ids.length ? new Set(ids) : null;
  } catch {
    // Storage unavailable (private mode / SSR), or a value someone else
    // wrote. Either way we know nothing, which reads below as "unattributed"
    // and takes the conservative branch rather than clearing anyone's latch.
  }
  return null;
}

function writeStoredOwners(ids) {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      if (ids.size) {
        window.sessionStorage.setItem(OWNERS_KEY, JSON.stringify([...ids]));
      } else {
        window.sessionStorage.removeItem(OWNERS_KEY);
      }
    }
  } catch {
    /* storage unavailable — ignore */
  }
}

// The union of what this JS context has seen and what survived a reload.
function knownOwners() {
  const stored = readStoredOwners();
  if (!stored) return launchedUserIds;
  return new Set([...launchedUserIds, ...stored]);
}

export function markCheckoutLaunched(userId) {
  const owners = knownOwners();
  // A launch with no uid to attribute it to still has to latch. It is recorded
  // as unattributed, which reads below as "could be anyone's" — the safe
  // direction, since the alternative is offering a second checkout.
  if (userId) owners.add(userId);
  launchedUserIds = owners;
  writeStoredOwners(owners);
  if (!userId) unattributedLaunch = true;
}

/**
 * Has this session already sent `userId` to a checkout?
 * @param {string} [userId] the account asking. Omitted means "anyone", which
 *   keeps the answer conservative for callers with no uid to hand.
 */
export function wasCheckoutLaunched(userId) {
  // The persisted half of "was one launched at all". Web checkout is a
  // full-page redirect to Stripe, so the module state above is wiped by the
  // time the user comes back — and the entitlement is not there yet either,
  // because the webhook can lag the return by up to the confirmation window.
  // hasArmedCheckout reads the same sessionStorage marker the
  // billing-confirmation flow already keeps across that redirect, which is
  // exactly the missing span.
  const armed = hasArmedCheckout();
  const owners = knownOwners();
  if (!owners.size && !unattributedLaunch && !armed) return false;
  if (!userId) return true;
  if (owners.has(userId)) return true;

  // Nothing was recorded for this account. An armed checkout that no account
  // claims could still be theirs — the Account upgrade buttons arm one without
  // coming through this module — so it keeps the conservative answer. Erring
  // toward "yes" costs a plan step the user can reach again from Account; the
  // reverse mistake charges someone twice.
  return (armed || unattributedLaunch) && !owners.size;
}

// Test-only: module state outlives a component, so each test needs a fresh
// session.
export function _resetCheckoutSessionForTests() {
  launchedUserIds = new Set();
  unattributedLaunch = false;
  writeStoredOwners(new Set());
}
